import { useQuery } from '@tanstack/react-query';

import { apiRequest } from '@/lib/api/client';

/**
 * Background queues, mirroring ZazzBackend's BullMQ queue names exactly.
 *
 * Source of truth: Mesh-BE-2.0/ZazzBackend/queues/{,v2/}*.js — each is a
 * `createBullMqQueue('<name>')` call. The names are kebab-case and are the wire
 * contract between services; do not "tidy" them into camelCase.
 */
export const QUEUE_NAMES = [
  'drafts',
  'scoring',
  'waiver',
  'trade',
  'notifications',
  'player-sync',
  'player-ingest',
  'player-research',
  'projection',
  'lineup-lock-cache',
  'matchup-context',
  'week-engine',
  'stat-correction',
  'stat-ingestion',
  'taunt-window',
  'runtime-lifecycle',
] as const;

export type QueueName = (typeof QUEUE_NAMES)[number];

export type QueueStatus = 'healthy' | 'degraded' | 'backed_up';

export interface QueueSnapshot {
  name: QueueName;
  depth: number;
  active: number;
  completed: number;
  failed: number;
  /**
   * Jobs waiting on a timer, which is where a repeatable scheduler's next tick lives. Worth
   * showing: on a queue with no consumer this is the ONLY count that moves — nothing promotes a
   * delayed job without a worker, so `depth` stays 0 and the queue reads as idle.
   */
  delayed: number;
  /**
   * The most recent COMPLETED job still retained in Redis. Not the same as "when the scheduler
   * last ticked" — see the scheduler list for that, which reads the worker's own heartbeat.
   */
  lastRunAt: string | null;
}

export interface QueueHealthPayload {
  status: QueueStatus;
  queues: QueueSnapshot[];
  fetchedAt: string;
}

export const queueHealthQuery = {
  queryKey: ['queue-health'] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    apiRequest<QueueHealthPayload>('/api/dataops/queue-health', { signal }),
  // Worker backlog is the canary for "something is wrong right now", so this
  // polls regardless of which screen the operator is on.
  refetchInterval: 20_000,
};

export function useQueueHealth() {
  return useQuery(queueHealthQuery);
}

/**
 * Severity rank for a queue.
 *
 * `failed: -1` is the sentinel for "could not be read at all", which is the WORST
 * state, not the mildest — but a naive `b.failed - a.failed` sorts it below every
 * healthy queue and then slices it off the end. Ranking explicitly is what keeps an
 * unreadable queue at the top where it belongs.
 */
export function queueSeverity(queue: { failed: number; depth: number }): number {
  if (queue.failed < 0) return 0;
  if (queue.failed > 0) return 1;
  if (queue.depth > 50) return 2;
  if (queue.depth > 0) return 3;
  return 4;
}

export function compareQueues(
  a: { failed: number; depth: number; name: string },
  b: { failed: number; depth: number; name: string },
): number {
  const bySeverity = queueSeverity(a) - queueSeverity(b);
  if (bySeverity !== 0) return bySeverity;
  if (a.failed !== b.failed) return b.failed - a.failed;
  if (a.depth !== b.depth) return b.depth - a.depth;
  return a.name.localeCompare(b.name);
}

export const QUEUE_STATUS_STYLE: Record<
  QueueStatus,
  { bg: string; border: string; dot: string; text: string; label: string }
> = {
  healthy: {
    bg: 'rgba(var(--ok-rgb), 0.1)',
    border: 'rgba(var(--ok-rgb), 0.34)',
    dot: 'var(--ok)',
    text: 'var(--ok)',
    label: 'queues ok',
  },
  degraded: {
    bg: 'rgba(var(--warn-rgb), 0.12)',
    border: 'rgba(var(--warn-rgb), 0.4)',
    dot: 'var(--warn)',
    text: 'var(--warn)',
    label: 'queues slow',
  },
  backed_up: {
    bg: 'rgba(var(--err-rgb), 0.12)',
    border: 'rgba(var(--err-rgb), 0.45)',
    dot: 'var(--err)',
    text: 'var(--err-soft)',
    label: 'queues backed up',
  },
};
